/**
 * Iter 237 (Lot 3) — Page admin Pointage GPS/SMS.
 *
 * Liste les pointages (collection tracking_punches) du tenant, permet
 * d'approuver/refuser ceux hors géofence. Affiche les terminaux + KPIs.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { TerminalsManager } from "../components/tracking/TerminalsManager";
import { HoursExport } from "../components/tracking/HoursExport";
import { Button } from "../components/ui/button";
import {
  Clock, CheckCircle2, XCircle, AlertTriangle, Loader2,
  RefreshCw, QrCode, WifiOff,
} from "lucide-react";

export default function AdminPunchPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [punches, setPunches] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => (new URLSearchParams(window.location.search).get("filter") === "pending_approval" ? "pending_approval" : "all")); // all | pending_approval | today

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get("/tracking/punches?limit=200").catch(() => ({ data: { punches: [] } })),
        api.get("/tracking/terminals").catch(() => ({ data: { terminals: [] } })),
      ]);
      const punchesList = Array.isArray(p.data) ? p.data : (p.data?.punches || p.data?.items || []);
      const termsList = Array.isArray(t.data) ? t.data : (t.data?.terminals || t.data?.items || []);
      setPunches(punchesList);
      setTerminals(termsList);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (punchId, decision) => {
    try {
      await api.post(`/tracking/punches/${punchId}/approve`, { decision });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  const filtered = useMemo(() => {
    const list = Array.isArray(punches) ? punches : [];
    if (filter === "pending_approval") return list.filter(needsApproval);
    if (filter === "today") {
      const today = new Date().toISOString().slice(0, 10);
      return list.filter((p) => (p?.start_at || "").startsWith(today));
    }
    return list;
  }, [punches, filter]);

  const todayCount = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (Array.isArray(punches) ? punches : []).filter((p) => (p?.start_at || "").startsWith(today)).length;
  }, [punches]);
  const pendingCount = useMemo(() => (Array.isArray(punches) ? punches : []).filter(needsApproval).length, [punches]);

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="admin-punch-page">
      <PageHeader
        icon={<Clock />}
        title="Pointage GPS / SMS"
        description="Suivi des pointages de l'équipe, géofence, validation manager"
        testId="admin-punch-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label="Total pointages" value={punches.length} color="text-white" />
        <KPI label="Aujourd'hui" value={todayCount} color="text-cyan-400" />
        <KPI label="À valider" value={pendingCount} color="text-amber-400" Icon={AlertTriangle} />
        <KPI label="Terminaux" value={terminals.length} color="text-emerald-400" Icon={QrCode} />
      </div>

      <HoursExport />

      <div className="flex items-center gap-2 mb-4">
        {["all", "today", "pending_approval"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-lg font-bold ${filter === f ? "bg-[#f7c935] text-[#0b0811]" : "bg-[#14101c] text-slate-300 border border-white/5 hover:bg-[#1a1424]"}`}
            data-testid={`filter-${f}`}
          >
            {f === "all" && "Tous"}
            {f === "today" && "Aujourd'hui"}
            {f === "pending_approval" && `À valider${pendingCount > 0 ? ` (${pendingCount})` : ""}`}
          </button>
        ))}
        <div className="flex-1" />
        <Button onClick={load} size="sm" variant="ghost" className="text-xs" data-testid="refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rafraîchir
        </Button>
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Aucun pointage.<br />
            <span className="text-xs">
              Configurez CallMeBot WhatsApp dans <a href="/admin/settings?tab=api&section=time_tracking" className="text-cyan-400 hover:underline">Paramètres → API → Pointage salariés</a>.
            </span>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]" data-testid="punches-table">
            <thead className="bg-[#0b0811] text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left p-3">Employé</th>
                <th className="text-left p-3">Début</th>
                <th className="text-left p-3">Fin</th>
                <th className="text-left p-3">Durée</th>
                <th className="text-left p-3">Géofence</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const dur = p.stop_at && p.start_at ? Math.round((new Date(p.stop_at) - new Date(p.start_at)) / 60000) : null;
                const needsAction = needsApproval(p);
                const decision = p.manager_approval?.decision;
                const isQr = p.qr_verified || p.stop_qr_verified;
                const isOffline = !isQr && (p.source === "offline" || p.stop_source === "offline");
                return (
                  <tr key={p.punch_id} className={`border-t border-white/5 hover:bg-[#0b0811]/50 ${needsAction ? "bg-amber-500/5" : ""}`} data-testid={`punch-row-${p.punch_id}`}>
                    <td className="p-3 text-white">
                      <div className="flex items-center gap-2">
                        {p.user_name || p.user_email || p.user_id}
                        {isOffline && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold uppercase inline-flex items-center gap-1" data-testid={`offline-badge-${p.punch_id}`}><WifiOff className="w-3 h-3" /> Hors ligne</span>}
                        {isQr && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-bold uppercase inline-flex items-center gap-1" data-testid={`qr-badge-${p.punch_id}`}><QrCode className="w-3 h-3" /> QR</span>}
                      </div>
                    </td>
                    <td className="p-3 text-slate-300 text-xs">{p.start_at ? new Date(p.start_at).toLocaleString("fr-FR") : "—"}</td>
                    <td className="p-3 text-slate-300 text-xs">{p.stop_at ? new Date(p.stop_at).toLocaleString("fr-FR") : <span className="text-emerald-400">En cours</span>}</td>
                    <td className="p-3 text-slate-300">{dur != null ? `${Math.floor(dur / 60)}h ${dur % 60}m` : "—"}</td>
                    <td className="p-3">
                      <GeofenceBadge status={p.geofence_status || p.start_geofence_status} />
                    </td>
                    <td className="p-3 text-right">
                      {needsAction ? (
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => decide(p.punch_id, "approved")} className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" data-testid={`approve-${p.punch_id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 inline -mt-0.5" /> Valider
                          </button>
                          <button onClick={() => decide(p.punch_id, "rejected")} className="text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30" data-testid={`reject-${p.punch_id}`}>
                            <XCircle className="w-3.5 h-3.5 inline -mt-0.5" /> Refuser
                          </button>
                        </div>
                      ) : decision ? (
                        <span className={`text-[10px] uppercase font-bold ${decision === "approved" ? "text-emerald-400" : "text-rose-400"}`} title={p.manager_approval?.by_name ? `par ${p.manager_approval.by_name}` : ""} data-testid={`decision-${p.punch_id}`}>
                          {decision === "approved" ? "Validé" : "Refusé"}
                        </span>
                      ) : (
                        <span className="text-slate-500 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <TerminalsManager terminals={terminals} onChanged={load} />
    </div>
  );
}

const needsApproval = (p) => !!p && !p.manager_approval?.decision && (p.manager_approval_required || p.requires_approval || (!p.qr_verified && (p.geofence_status || p.start_geofence_status) === "outside"));

function KPI({ label, value, color = "text-white", Icon }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-3 flex items-center gap-3">
      {Icon && <Icon className={`w-5 h-5 ${color}`} />}
      <div>
        <div className={`text-2xl font-black ${color}`}>{value}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function GeofenceBadge({ status }) {
  if (status === "inside") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold uppercase">À l'intérieur</span>;
  if (status === "outside") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-bold uppercase">Hors zone</span>;
  if (status === "qr_verified") return <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 font-bold uppercase">QR poste</span>;
  return <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-400 font-bold uppercase">N/A</span>;
}
